import * as React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import { Crumb, Field, SectionCard, TextInput } from '../components/forms';
import { PageHeadActions } from '../components/nav';
import useCreateActivation from '../../Activations/components/hooks/useCreateActivation';
import useEditActivation from '../../Activations/components/hooks/useEditActivation';
import useActivation, { ActivationObject } from '../../Activations/components/hooks/useActivation';
import useLocationQuery from '../../Common/components/hooks/useLocationQuery';
import { SANDBOX_DATA } from '../lib/sandbox';
import useJobHelpers from '@/src/Jobs/components/hooks/useJobHelpers';

const SANDBOX_CITIES = ['Chicago, Illinois, United States', 'Milwaukee, Wisconsin, United States', 'Austin, Texas, United States', 'Spokane, Washington, United States', 'Seattle, Washington, United States', 'New York, New York, United States', 'Los Angeles, California, United States', 'San Francisco, California, United States'];
const sandboxPlaces = (q: string) => ({
    features: SANDBOX_CITIES.filter((c) => c.toLowerCase().includes(q.toLowerCase())).map((place_name) => ({ place_name })),
});

/*
 * One form for create (/activations/create, /jobs/:jobId/activation/create)
 * and edit (/activations/:id/edit). Payloads match the classic forms:
 * create posts {name, date, location, v3Folder, jobId?}; edit PUTs the
 * whole fetched activation with the edited fields.
 */

const pad = (n: number) => String(n).padStart(2, '0');
const toInputDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/* the classic day picker hands back local midnight */
const fromInputDate = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export default function ActivationFormV3() {
    const { id, jobId } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const { createActivation } = useCreateActivation();
    const { saveActivation } = useEditActivation(id ? +id : 0);
    const { getActivation } = useActivation();
    const { getJob } = useJobHelpers();
    const { getLocations } = useLocationQuery();

    /* create's untouched date is "now", as in the classic form */
    const [createdAt] = useState(() => new Date());
    const [activation, setActivation] = useState<ActivationObject | null>(null);
    const [loadState, setLoadState] = useState<'loading' | 'ready' | 'failed'>(isEdit ? 'loading' : 'ready');
    const [job, setJob] = useState<any>(null);

    const [name, setName] = useState('');
    const [date, setDate] = useState(() => toInputDate(createdAt));
    const [location, setLocation] = useState('');
    const [v3Folder, setV3Folder] = useState('');
    const [places, setPlaces] = useState<string[]>([]);
    const placesSeq = useRef(0);
    const listId = useId();

    const [saving, setSaving] = useState(false);
    const busy = useRef(false);
    const [error, setError] = useState('');

    const load = () => {
        if (!id) return;
        setLoadState('loading');
        getActivation(+id)
            .then((a) => {
                setActivation(a);
                setName(a.name ?? '');
                setDate(typeof a.date === 'string' ? a.date : toInputDate(new Date(a.date)));
                setLocation(a.location ?? '');
                setV3Folder(a.v3Folder ?? '');
                setLoadState('ready');
            })
            .catch(() => setLoadState('failed'));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!jobId) return;
        getJob(+jobId)
            .then(setJob)
            .catch(() => setJob(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    const onLocation = async (value: string) => {
        setLocation(value);
        if (!value) return;
        const seq = ++placesSeq.current;
        /* fake-data mode answers locally so typed text never leaves the browser */
        const response = SANDBOX_DATA ? sandboxPlaces(value) : await getLocations(value);
        if (seq !== placesSeq.current) return;
        if (Array.isArray(response?.features)) {
            setPlaces(response.features.map((place: { place_name: string }) => place.place_name));
        }
    };

    const submit = async () => {
        if (busy.current || !date) return;
        busy.current = true;
        setSaving(true);
        setError('');
        try {
            if (isEdit) {
                if (!activation) return;
                const original = activation.date as unknown as string;
                const ok = await saveActivation({
                    ...activation,
                    name,
                    date: date === original ? original : fromInputDate(date),
                    location,
                    v3Folder,
                });
                if (ok) navigate(-1);
                else setError('Couldn’t save the activation. Try again.');
            } else {
                const created = await createActivation({
                    name,
                    date: date === toInputDate(createdAt) ? createdAt : fromInputDate(date),
                    location,
                    v3Folder,
                    jobId: jobId ? +jobId : undefined,
                });
                if (created?.id) navigate(`/activations/${created.id}`);
                else setError('Couldn’t create the activation. Try again.');
            }
        } catch {
            setError(isEdit ? 'Couldn’t save the activation. Try again.' : 'Couldn’t create the activation. Try again.');
        } finally {
            busy.current = false;
            setSaving(false);
        }
    };

    const crumb = jobId ? (
        <Crumb label={job?.name ?? 'Job'} onClick={() => navigate(`/pipeline/${jobId}`)} />
    ) : (
        <Crumb label="Activations" onClick={() => navigate('/activations')} />
    );

    const title = isEdit ? 'Edit activation' : 'New activation';
    const sub = jobId ? (job ? `For Job: ${job.name}` : ' ') : isEdit ? (activation?.name || ' ') : 'Basic details about the activation';

    return (
        <AppShell>
            {crumb}
            <PageHeadActions title={title} sub={sub} />
            {loadState === 'loading' && <p className="quiet-empty">Loading activation…</p>}
            {loadState === 'failed' && (
                <div className="dets">
                    <p className="quiet-empty" style={{ marginBottom: 12 }}>Couldn’t load this activation.</p>
                    <button type="button" className="tb-btn" onClick={load}>
                        Retry
                    </button>
                </div>
            )}
            {loadState === 'ready' && (
                <form
                    className="dets"
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit();
                    }}
                >
                    <div className="stack">
                        <SectionCard title="Basic details">
                            <Field label="Name">
                                <TextInput
                                    name="name"
                                    placeholder="Activation name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </Field>
                            <Field label="Date" error={date ? undefined : 'Pick a date.'}>
                                <TextInput
                                    type="date"
                                    name="date"
                                    style={{ maxWidth: 180 }}
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </Field>
                            <Field label="Location" help="City — suggestions appear as you type.">
                                <TextInput
                                    name="location"
                                    list={listId}
                                    autoComplete="on"
                                    value={location}
                                    onChange={(e) => onLocation(e.target.value)}
                                />
                            </Field>
                            <Field label="V3 folder">
                                <TextInput
                                    name="v3Folder"
                                    placeholder="v3 folder name"
                                    value={v3Folder}
                                    onChange={(e) => setV3Folder(e.target.value)}
                                />
                            </Field>
                            <datalist id={listId}>
                                {places.map((place, i) => (
                                    <option key={i} value={place} />
                                ))}
                            </datalist>
                        </SectionCard>
                    </div>
                    {error && (
                        <p className="field-e" role="alert" style={{ marginTop: 12 }}>
                            {error}
                        </p>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                        <button type="button" className="tb-btn" onClick={() => navigate(-1)}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={saving || !date}>
                            {isEdit ? (saving ? 'Saving…' : 'Save activation') : saving ? 'Creating…' : 'Create activation'}
                        </button>
                    </div>
                </form>
            )}
        </AppShell>
    );
}
